import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { formatDeliveryMethod } from "../utils/statusUtils";

const styles = StyleSheet.create({
  page: { padding: 30, fontFamily: "Helvetica", fontSize: 10, color: "#222" },
  title: { fontSize: 16, fontWeight: "bold", marginBottom: 16, color: "#001e45" },
  section: { marginBottom: 12, borderWidth: 1, borderColor: "#ccc", borderRadius: 4, padding: 10 },
  sectionTitle: { fontSize: 12, fontWeight: "bold", marginBottom: 8, color: "#001e45" },
  row: { flexDirection: "row", marginBottom: 4 },
  label: { fontFamily: "Helvetica-Bold", width: "40%", color: "#444" },
  value: { width: "60%", color: "#222" },
});

const Row = ({ label, value }: { label: string; value: string }) =>
  value ? (
    <View style={styles.row}>
      <Text style={styles.label}>{label ? `${label}:` : ""}</Text>
      <Text style={styles.value}>{value}</Text>
    </View>
  ) : null;

const formatBillingOption = (option: string) => {
  switch (option) {
    case "patientPays": return "Patient Pays";
    case "doctorPays": return "Doctor Pays";
    default: return option || "N/A";
  }
};

interface OrderPdfProps {
  order: any;
  product: any;
  productOrder: any;
  patientInfo: any;
}

const OrderPdf: React.FC<OrderPdfProps> = ({ order, product, productOrder, patientInfo }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <Text style={styles.title}>Order Summary</Text>

      {/* Prescription Drug */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Prescription Drug</Text>
        <Row label="Name" value={product?.name} />
        <Row label="Type" value={product?.type} />
        <Row label="Dosage Form" value={product?.dosageForm} />
        <Row label="Strength" value={productOrder?.strength} />
        <Row label="Duration" value={productOrder?.duration} />
        <Row label="Quantity" value={String(productOrder?.quantity ?? "")} />
        <Row label="Directions" value={productOrder?.directions} />
        <Row label="Days of Supply" value={String(productOrder?.days_of_supply ?? "")} />
        <Row label="Refills" value={String(productOrder?.refills ?? "")} />
      </View>

      {/* Patient Information */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Patient Information</Text>
        <Row label="Name" value={patientInfo?.name} />
        <Row label="Email" value={patientInfo?.email} />
        <Row label="Phone" value={patientInfo?.phone} />
        <Row label="Date of Birth" value={patientInfo?.dob ? (() => { const [y, m, d] = patientInfo.dob.split('T')[0].split('-'); return new Date(parseInt(y), parseInt(m) - 1, parseInt(d)).toLocaleDateString(); })() : ""} />
        {patientInfo?.addresses?.map((addr: any, i: number) => (
          <Row key={i} label={i === 0 ? "Address" : ""} value={`${addr.street}, ${addr.city}, ${addr.state} ${addr.zip}`} />
        ))}
      </View>

      {/* Provider Information */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Provider Information</Text>
        <Row label="Name" value={order?.provider?.name || "N/A"} />
        <Row label="Email" value={order?.provider?.email || "N/A"} />
        <Row label="Phone" value={order?.provider?.phone || "N/A"} />
        <Row label="NPI" value={order?.provider?.npi ? String(order.provider.npi) : "N/A"} />
        <Row label="Clinic Name" value={order?.provider?.practiceName || "N/A"} />
      </View>

      {/* Pricing */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Pricing</Text>
        <Row label="Order Number" value={order?.id != null ? `#${order.id}` : "N/A"} />
        <Row label="Retail Price" value={productOrder?.price != null ? `$${(productOrder.price / 100 * (productOrder.quantity ?? 1)).toFixed(2)}` : "N/A"} />
        {productOrder?.provider_price != null && (
          <Row label="Provider Price" value={`$${(productOrder.provider_price / 100 * (productOrder.quantity ?? 1)).toFixed(2)}`} />
        )}
      </View>

      {/* Fulfillment and Delivery */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Fulfillment and Delivery</Text>
        <Row label="Delivery Method" value={formatDeliveryMethod(order?.deliveryMethod)} />
        <Row label="Fulfillment Address" value={order?.fulfillment_address || "N/A"} />
        <Row label="Priority" value={order?.isAsap ? "ASAP" : ""} />
        {!order?.isAsap && order?.needBy && (
          <Row label="Need By Date" value={new Date(order.needBy.includes('T') ? order.needBy : order.needBy + 'T12:00:00').toLocaleDateString("en-US", {
            year: "numeric", month: "short", day: "numeric"
          })} />
        )}
        <Row label="Billing Option" value={formatBillingOption(order?.billingOption)} />
      </View>

      {/* Order Special Instructions */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Order Special Instructions</Text>
        <Text>{order?.specialInstructions || "N/A"}</Text>
      </View>
    </Page>
  </Document>
);

export default OrderPdf;
